import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useTransactions } from "@/lib/useTransactions";
import { useAuth } from "@/context/AuthContext";
import { LiveTimer } from "@/components/LiveTimer";
import { fmtDuration, fmtTimer, useElapsed, colorState } from "@/lib/time";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SlaTargetsPanel } from "@/components/SlaTargetsPanel";
import { Users, Clock, PackageCheck, AlertTriangle, Timer, Target, CheckCircle2, Flame, Mail } from "lucide-react";

function StatCard({ label, value, accent, icon: Icon, testId }) {
  return (
    <div className="bg-card border border-border p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        {Icon && <Icon size={16} style={{ color: accent }} />}
      </div>
      <div className="font-display text-4xl font-extrabold mt-2 leading-none" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

function OverdueRow({ t, employees, onAction }) {
  const seconds = useElapsed(t.stage_start);
  const state = t.stage ? colorState(seconds, t.sla) : "grey";
  const color = { green: "#22C55E", amber: "#F59E0B", red: "#FF3B30", darkred: "#FF3B30", grey: "#52525B" }[state];
  const [assignId, setAssignId] = useState("");
  const [showOverride, setShowOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [overrideStatus, setOverrideStatus] = useState("DELIVERED");

  const reassign = async () => {
    if (!assignId) return toast.error("Select an employee");
    try {
      await api.post(`/transactions/${t.id}/reassign`, { user_id: assignId });
      toast.success("Reassigned"); onAction();
    } catch (e) { toast.error(apiError(e)); }
  };
  const override = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/transactions/${t.id}/override`, { new_status: overrideStatus, reason });
      toast.success("Status overridden"); setShowOverride(false); onAction();
    } catch (e) { toast.error(apiError(e)); }
  };

  const assignedName = t.service_started_by_name || t.warehouse_accepted_by_name || "Unassigned";

  return (
    <tr className="border-b border-border hover:bg-secondary/40 transition-colors">
      <td className="px-3 py-3 font-mono-t font-bold text-white">{t.kin_number}</td>
      <td className="px-3 py-3 text-sm">{t.stage_label || t.current_status}</td>
      <td className="px-3 py-3 text-sm">{assignedName}</td>
      <td className="px-3 py-3 font-mono-t font-bold" style={{ color }}>{fmtTimer(seconds)}</td>
      <td className="px-3 py-3 text-sm text-muted-foreground">{fmtDuration(t.total_wait_seconds)}</td>
      <td className="px-3 py-3 text-center">{t.alert_count || 0}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <Select value={assignId} onValueChange={setAssignId}>
            <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="Reassign to…" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8" onClick={reassign} data-testid={`reassign-${t.kin_number}`}>Assign</Button>
          <Button size="sm" variant="destructive" className="h-8" onClick={() => setShowOverride(true)} data-testid={`override-${t.kin_number}`}>Override</Button>
        </div>
      </td>
      <Dialog open={showOverride} onOpenChange={setShowOverride}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="font-display text-2xl">Override Status — {t.kin_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={overrideStatus} onValueChange={setOverrideStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["WAITING","ACCEPTED_BY_WAREHOUSE","PREPARED","SERVICE_STARTED","DELIVERED","ESCALATED","CANCELLED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Reason for override (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverride(false)}>Cancel</Button>
            <Button variant="destructive" onClick={override}>Confirm Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </tr>
  );
}

export default function Supervisor() {
  const { currentStore } = useAuth();
  const { txns, reload } = useTransactions();
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    if (!currentStore) return;
    const load = () => {
      api.get(`/dashboard/store/${currentStore.id}`).then((r) => setStats(r.data)).catch(() => {});
      api.get(`/users?store_id=${currentStore.id}`).then((r) => setEmployees(r.data.users)).catch(() => {});
      api.get(`/reports/overdue-digest?store_id=${currentStore.id}`).then((r) => setDigest(r.data)).catch(() => {});
    };
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [currentStore]);

  const overdue = txns.filter((t) => t.is_overdue);
  const assignable = employees.filter((e) => ["warehouse_agent", "front_desk_agent", "supervisor"].includes(e.role));

  const emailDigest = async () => {
    try {
      const { data } = await api.post("/reports/digest-email", { store_id: currentStore?.id });
      toast.success(`Digest emailed to ${data.recipient}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <DashboardLayout title="Supervisor">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Waiting" value={stats?.waiting ?? "—"} accent="#F59E0B" icon={Clock} testId="stat-waiting" />
        <StatCard label="Preparing" value={stats?.preparing ?? "—"} accent="#3B82F6" icon={Timer} />
        <StatCard label="Prepared" value={stats?.prepared ?? "—"} accent="#22C55E" icon={PackageCheck} />
        <StatCard label="In Service" value={stats?.in_service ?? "—"} accent="#3B82F6" icon={Users} />
        <StatCard label="Overdue" value={stats?.overdue ?? "—"} accent="#FF3B30" icon={AlertTriangle} testId="stat-overdue" />
        <StatCard label="Served Today" value={stats?.served_today ?? "—"} accent="#22C55E" icon={CheckCircle2} />
        <StatCard label="Avg Wait" value={fmtDuration(stats?.avg_wait_today)} accent="#fff" icon={Clock} />
        <StatCard label="Longest Active" value={fmtDuration(stats?.longest_active_wait)} accent="#F59E0B" />
        <StatCard label="Within Target" value={`${stats?.within_target_pct ?? 100}%`} accent="#22C55E" icon={Target} />
        <StatCard label="Active Staff" value={employees.filter((e)=>e.is_active).length} accent="#fff" icon={Users} />
      </div>

      <div className="bg-card border border-border">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <AlertTriangle size={18} className="text-alert-red" />
          <span className="font-display text-xl font-bold uppercase tracking-wide">Overdue Transactions</span>
          <span className="font-mono-t text-sm text-alert-red ml-2">{overdue.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="overdue-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="px-3 py-2">KIN</th><th className="px-3 py-2">Stage</th><th className="px-3 py-2">Employee</th>
                <th className="px-3 py-2">In Stage</th><th className="px-3 py-2">Total Wait</th><th className="px-3 py-2">Alerts</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {overdue.map((t) => <OverdueRow key={t.id} t={t} employees={assignable} onAction={reload} />)}
              {overdue.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No overdue transactions. All within target.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card border border-border mt-6" data-testid="overdue-digest">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Flame size={18} className="text-alert-amber" />
          <span className="font-display text-xl font-bold uppercase tracking-wide">Today's Overdue Digest</span>
          <span className="font-mono-t text-sm text-alert-amber ml-2">{digest?.total ?? 0} alerts</span>
          <Button size="sm" variant="outline" className="ml-auto gap-1" onClick={emailDigest} data-testid="email-digest-btn"><Mail size={14} /> Email Me</Button>
        </div>
        <div className="p-3 space-y-2">
          {(digest?.rows || []).map((r, i) => (
            <div key={i} className="flex items-center justify-between bg-secondary/40 px-4 py-3 border border-border" data-testid={`digest-row-${i}`}>
              <div>
                <div className="font-semibold">{r.full_name}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">{r.role !== "—" ? r.role.replace(/_/g, " ") : "not accepted in time"} · {r.stages.join(", ")}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-3xl font-extrabold text-alert-red leading-none">{r.count}</div>
                <div className="text-[10px] uppercase text-muted-foreground">{r.acknowledged} ack'd</div>
              </div>
            </div>
          ))}
          {(!digest || digest.rows.length === 0) && (
            <div className="text-center text-muted-foreground py-6 text-sm">No overdue alerts recorded today. Great work.</div>
          )}
        </div>
      </div>
      {currentStore && <SlaTargetsPanel store={currentStore} />}
    </DashboardLayout>
  );
}
